<template>
  <BaseCard title="Presupuesto" :noHover="true" :collapsible="true">
    <div class="planes-layout">
      <!-- Columna izquierda: lista de planes -->
      <div class="planes-col">
    <h4 class="col-titulo">Planes de tratamiento</h4>
    <!-- Lista de planes -->
    <div class="planes-lista" v-if="planes.length">
      <button
        v-for="plan in planes"
        :key="plan.id"
        type="button"
        class="plan-card"
        :class="{ activo: plan.id === selectedPlanId }"
        @click="seleccionarPlan(plan.id)"
      >
        <div class="plan-top">
          <span class="dot" :class="dotEstado(plan.estado)"></span>
          <span class="plan-num">Plan Nº {{ plan.num }}</span>
          <span class="pill" :class="pillEstado(plan.estado)">{{ labelEstado(plan.estado) }}</span>
        </div>
        <div class="plan-sub">
          {{ plan.items.length ? plan.items.length + (plan.items.length === 1 ? ' tratamiento' : ' tratamientos') + ' · ' + formatCurrency(planTotal(plan)) : 'Vacío' }}
        </div>
        <div class="plan-meta">
          <span class="plan-fecha">{{ formatDate(plan.fecha, true) }}</span>
          <span class="plan-saldo">Saldo: <b>{{ formatCurrency(planSaldo(plan)) }}</b></span>
        </div>
      </button>
    </div>
    <p v-else class="empty-msg">Este paciente todavía no tiene planes. Creá el primero.</p>

    <button class="btn-nuevo-plan" @click="nuevoPlan">＋ Nuevo plan</button>
      </div>

      <!-- Columna derecha: detalle del plan -->
      <div class="detalle-col">
    <!-- Detalle del plan seleccionado -->
    <div v-if="selectedPlan" class="plan-detalle">
      <div class="detalle-head">
        <div class="detalle-titulo">
          <h3>Plan Nº {{ selectedPlan.num }}</h3>
          <span class="detalle-fecha">{{ formatDate(selectedPlan.fecha, true) }}</span>
        </div>
        <label class="estado-sel-wrap">
          Estado:
          <select class="estado-sel" :value="selectedPlan.estado" @change="cambiarEstado($event.target.value)">
            <option value="borrador">Borrador</option>
            <option value="pendiente">En curso</option>
            <option value="finalizado">Finalizado</option>
            <option value="cancelado">Cancelado</option>
          </select>
        </label>
      </div>

      <!-- Barra de cobro en bloque -->
      <div class="bulkbar" v-if="bulkMode">
        <span class="bulk-txt">Cobrar {{ bulkMode === 'sel' ? 'seleccionados' : 'todo el plan' }} · falta <b>{{ formatCurrency(faltaBulk) }}</b></span>
        <input class="inp" type="number" min="0" v-model.number="bulkMonto" placeholder="Monto" />
        <select v-if="facturacion" class="inp" v-model="bulkMetodo">
          <option v-for="(lbl, k) in METODOS" :key="k" :value="k">{{ lbl }}</option>
        </select>
        <button class="btn-mini pri" @click="confirmarCobroBloque">Confirmar cobro</button>
        <button class="btn-mini" @click="bulkMode = null">Cancelar</button>
      </div>
      <div class="bulkbar" v-else>
        <button type="button" class="help" :class="{ activo: mostrarAyudaCobro }" aria-label="Ayuda de cobro" @click="mostrarAyudaCobro = !mostrarAyudaCobro">i</button>
        <button class="btn-mini" :disabled="!nSeleccionadosCobrables" @click="iniciarCobro('sel')">
          Cobrar seleccionados{{ nSeleccionadosCobrables ? ' (' + nSeleccionadosCobrables + ')' : '' }}
        </button>
        <button class="btn-mini pri" :disabled="planSaldo(selectedPlan) <= 0" @click="iniciarCobro('all')">Saldar plan</button>
      </div>
      <p v-if="!bulkMode && mostrarAyudaCobro" class="ayuda-cobro">
        Los tratamientos tildados se cobran en bloque y son los que salen al imprimir. Sin tildar nada, imprime el plan completo.
      </p>

      <!-- Tratamientos del plan -->
      <div class="items" v-if="selectedPlan.items.length">
        <div class="item" v-for="item in selectedPlan.items" :key="item.id" :class="{ sel: picked[item.id] }">
          <div class="item-row" @click="toggleItem(item.id)">
            <input class="chk" type="checkbox" :checked="!!picked[item.id]" @click.stop="togglePick(item.id)" />
            <div class="item-main">
              <div class="item-nombre">{{ item.nombre }}</div>
              <div class="item-badges">
                <span class="badge-sm" :class="'b-' + estadoItem(item)">{{ labelEstadoItem(item) }}</span>
                <span v-if="item.descuento > 0" class="badge-sm b-desc">-{{ item.descuento }}%</span>
                <span v-if="faltaItem(item) > 0 && estadoItem(item) !== 'pendiente'" class="falta">Falta <b>{{ formatCurrency(faltaItem(item)) }}</b></span>
              </div>
            </div>
            <div class="item-precio">
              <template v-if="item.descuento > 0">
                <span class="tach">{{ formatCurrency(item.precio) }}</span>{{ formatCurrency(precioFinalItem(item)) }}
              </template>
              <template v-else>{{ formatCurrency(item.precio) }}</template>
            </div>
            <span class="exp">{{ expandedItems[item.id] ? '▲' : '▼' }}</span>
          </div>

          <div class="item-body" v-if="expandedItems[item.id]">
            <!-- Descuento -->
            <div class="row-line">
              <span class="mini-label">Descuento</span>
              <input class="inp chico" type="number" min="0" max="100" :value="item.descuento || 0"
                @change="cambiarDescuento(item, $event.target.value)" /> %
            </div>

            <!-- Agregar pago individual -->
            <div class="row-line" v-if="faltaItem(item) > 0">
              <span class="mini-label">Agregar pago</span>
              <input class="inp inp-monto" type="number" min="0" placeholder="Monto…" v-model.number="pagosTemp[item.id]" @keyup.enter="registrarPago(item)" />
              <select v-if="facturacion" class="inp" v-model="pagoMetodos[item.id]">
                <option v-for="(lbl, k) in METODOS" :key="k" :value="k">{{ lbl }}</option>
              </select>
              <button class="btn-mini pri" :disabled="!pagosTemp[item.id]" @click="registrarPago(item)">Registrar</button>
              <span class="falta">Falta <b>{{ formatCurrency(faltaItem(item)) }}</b></span>
            </div>

            <!-- Pagos -->
            <div class="mini-label">Pagos</div>
            <div class="pagos-hist">
              <template v-if="item.pagos.length">
                <div class="ph-row" v-for="pago in item.pagos" :key="pago.id">
                  <template v-if="moving && moving.itemId === item.id && moving.pagoId === pago.id">
                    <div class="mover-inline">
                      <span class="mv-lbl">Mover {{ formatCurrency(pago.monto) }} a:</span>
                      <template v-if="otrosItems(item).length">
                        <select class="inp" v-model="moveDest">
                          <option v-for="d in otrosItems(item)" :key="d.id" :value="d.id">{{ d.nombre }} (falta {{ formatCurrency(faltaItem(d)) }})</option>
                        </select>
                        <button class="btn-mini pri" @click="confirmarMover(item, pago)">Mover</button>
                      </template>
                      <span v-else class="mv-lbl">No hay otro tratamiento.</span>
                      <button class="btn-mini" @click="moving = null">Cancelar</button>
                    </div>
                  </template>
                  <template v-else>
                    <span class="ph-fecha">{{ formatDate(pago.fecha) }}</span>
                    <div class="ph-mid">
                      <span class="ph-desc">
                        <span v-if="pago.esSaldoAnterior" class="ph-tag">Saldo anterior</span>
                        <span v-else-if="facturacion && pago.metodo" class="metodo-chip">{{ METODOS[pago.metodo] }}</span>
                        <span v-else class="ph-tag">Abono</span>
                      </span>
                      <span class="ph-monto">{{ formatCurrency(pago.monto) }}</span>
                    </div>
                    <div class="ph-acciones">
                      <button class="ph-move" title="Mover a otro tratamiento" @click="iniciarMover(item, pago)">↔</button>
                      <button class="ph-x" title="Borrar pago" @click="eliminarPago(item, pago)">×</button>
                    </div>
                  </template>
                </div>
              </template>
              <div v-else class="ph-row vacio">Sin pagos.</div>
            </div>

            <button v-if="pagadoItem(item) === 0" class="item-quitar" @click="quitarTratamiento(item)">🗑️ Quitar tratamiento</button>
          </div>
        </div>
      </div>
      <p v-else class="empty-msg">Este plan no tiene tratamientos. Agregá uno.</p>

      <!-- Resumen del plan -->
      <div class="resumen">
        <div class="resumen-card"><span class="label">Total</span><span class="valor">{{ formatCurrency(planTotal(selectedPlan)) }}</span></div>
        <div class="resumen-card"><span class="label">Pagado</span><span class="valor verde">{{ formatCurrency(planPagado(selectedPlan)) }}</span></div>
        <div class="resumen-card"><span class="label">Saldo</span><span class="valor" :class="planSaldo(selectedPlan) > 0 ? 'ambar' : 'verde'">{{ formatCurrency(planSaldo(selectedPlan)) }}</span></div>
      </div>

      <!-- Acciones del plan -->
      <div class="acciones">
        <button class="btn-action" @click="imprimirPlan">🖨️ Imprimir</button>
        <button class="btn-action" @click="abrirBiblioteca">＋ Agregar tratamiento</button>
        <button v-if="!planTienePagos(selectedPlan)" class="btn-action peligro" @click="deshacerPlan">↩️ Deshacer plan</button>
      </div>
    </div>
      <p v-else class="detalle-vacio">Tocá un plan para ver o cobrar sus tratamientos.</p>
      </div>
    </div>

    <!-- Modal Biblioteca de Tratamientos -->
    <div v-if="mostrarBiblioteca" class="modal-overlay" @click.self="cerrarBiblioteca">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Biblioteca de Tratamientos</h3>
          <button class="btn-close" @click="cerrarBiblioteca">×</button>
        </div>

        <div class="crear-tratamiento">
          <h4 class="section-title">Crear Nuevo</h4>
          <div class="form-row">
            <input v-model="nuevoTratamiento.nombre" id="trat-nombre" name="trat-nombre" autocomplete="off" placeholder="Nombre (ej. Extracción)" class="input-modern" />
            <input v-model.number="nuevoTratamiento.precio" id="trat-precio" name="trat-precio" autocomplete="off" type="number" min="0" placeholder="Precio" class="input-modern" />
            <button @click="crearTratamiento" :disabled="!nuevoTratamiento.nombre || !nuevoTratamiento.precio" class="btn-save-modern">Guardar</button>
          </div>
        </div>

        <div class="lista-global">
          <input class="buscar-biblio" type="search" v-model="busquedaBiblio" autocomplete="off" placeholder="Buscar tratamiento…" />
          <div v-if="tratamientosFiltrados.length" class="global-items">
            <div v-for="t in tratamientosFiltrados" :key="t.id" class="global-item">
              <div class="global-info">
                <div v-if="editingTreatmentId === t.id" class="edit-global-row">
                  <input v-model="tempTreatment.nombre" :id="'trat-edit-nombre-' + t.id" :name="'trat-edit-nombre-' + t.id" autocomplete="off" class="input-small" placeholder="Nombre" />
                  <input v-model.number="tempTreatment.precio" :id="'trat-edit-precio-' + t.id" :name="'trat-edit-precio-' + t.id" autocomplete="off" type="number" min="0" class="input-small" placeholder="Precio" />
                  <button class="btn-icon-action" @click="guardarEdicionTratamiento">✅</button>
                  <button class="btn-icon-action" @click="cancelarEdicionTratamiento">❌</button>
                </div>
                <div v-else class="info-display">
                  <span class="t-nombre">{{ t.nombre }}</span>
                  <span class="t-precio">{{ formatCurrency(t.precio) }}</span>
                </div>
              </div>
              <div class="global-actions">
                <template v-if="editingTreatmentId !== t.id">
                  <button class="btn-add-small" @click="agregarAlPlan(t)">Agregar</button>
                  <button class="btn-icon-action" @click="iniciarEdicionTratamiento(t)">✏️</button>
                  <button class="btn-icon-action btn-danger" @click="store.eliminarTratamientoGlobal(t.id)">🗑️</button>
                </template>
              </div>
            </div>
          </div>
          <p v-else class="empty-msg">{{ store.tratamientos.length ? 'Sin resultados.' : 'No hay tratamientos en la biblioteca.' }}</p>
        </div>
      </div>
    </div>
  </BaseCard>

  <ModalPagoPendiente :mostrar="mostrarModalPago" @cerrar="cerrarModalPago" />
</template>

<script setup>
import BaseCard from './BaseCard.vue';
import ModalPagoPendiente from './ModalPagoPendiente.vue';
import { ref, computed, watch, onMounted } from 'vue';
import { usePacientesStore } from '../stores/pacientes';
import { useUsuariosStore } from '@/stores/usuarios';
import { usePagoVerificacion } from '@/composables/usePagoVerificacion';
import { useToast } from '@/composables/useToast';
import { useScrollLock } from '@/composables/useScrollLock';
import { imprimirDocumentoHTML } from '@/utils/impresion';
import { useDialog } from '@/composables/useDialog';
import {
  precioFinalItem, pagadoItem, faltaItem, estadoItem,
  planTotal, planPagado, planSaldo, planTienePagos,
} from '@/utils/planesPresupuesto';

const props = defineProps({
  paciente: { type: Object, required: true },
});

const store = usePacientesStore();
const usuariosStore = useUsuariosStore();
const { mostrarModalPago, verificarPermisoPago, cerrarModalPago } = usePagoVerificacion();
const { success: toastSuccess } = useToast();
const { alerta, confirmar } = useDialog();

const METODOS = { efectivo: '💵 Efectivo', debito: '💳 Débito', credito: '💳 Crédito', transferencia: '📲 Transferencia' };

const selectedPlanId = ref(null);
const expandedItems = ref({});
const picked = ref({});
const pagosTemp = ref({});
const pagoMetodos = ref({});
const bulkMode = ref(null);
const bulkMonto = ref(null);
const bulkMetodo = ref('efectivo');
const mostrarAyudaCobro = ref(false); // el ⓘ del cobro en bloque: toca para ver/ocultar (sirve en cel)
const moving = ref(null);   // { itemId, pagoId }
const moveDest = ref(null);
const mostrarBiblioteca = ref(false);
const nuevoTratamiento = ref({ nombre: '', precio: '' });
const busquedaBiblio = ref('');
const editingTreatmentId = ref(null);
const tempTreatment = ref({ nombre: '', precio: '' });

useScrollLock(() => mostrarBiblioteca.value);

// Al abrir la ficha, migrar el presupuesto viejo a planes si hace falta (atómico, online-only).
onMounted(() => { store.migrarPlanesEnFirestore(props.paciente.id); });

const planes = computed(() => props.paciente.presupuestos || []);
const facturacion = computed(() => usuariosStore.usuarioActual?.mostrarFacturacion === true);

// Biblioteca filtrada por el buscador (por nombre, sin distinguir mayúsculas/acentos).
const tratamientosFiltrados = computed(() => {
  const q = busquedaBiblio.value.trim().toLowerCase();
  if (!q) return store.tratamientos;
  return store.tratamientos.filter((t) => (t.nombre || '').toLowerCase().includes(q));
});

const selectedPlan = computed(() => planes.value.find((p) => p.id === selectedPlanId.value) || null);

// Al entrar no se abre ningún plan: se ve solo la lista y el plan se abre al tocarlo.
// Solo cerramos la selección si el plan que estaba abierto dejó de existir.
watch(planes, (lista) => {
  if (selectedPlanId.value !== null && !lista.some((p) => p.id === selectedPlanId.value)) {
    selectedPlanId.value = null;
  }
}, { immediate: true });

const faltaBulk = computed(() => {
  if (!selectedPlan.value) return 0;
  const items = bulkMode.value === 'sel'
    ? selectedPlan.value.items.filter((it) => picked.value[it.id] && faltaItem(it) > 0)
    : selectedPlan.value.items.filter((it) => faltaItem(it) > 0);
  return items.reduce((s, it) => s + faltaItem(it), 0);
});

const nSeleccionadosCobrables = computed(() => {
  if (!selectedPlan.value) return 0;
  return selectedPlan.value.items.filter((it) => picked.value[it.id] && faltaItem(it) > 0).length;
});

// --- Presentación ---
function formatCurrency(v) {
  return new Intl.NumberFormat('es-UY', { style: 'currency', currency: 'UYU', maximumFractionDigits: 0 }).format(Math.round(v || 0));
}
function formatDate(isoStr, conAnio = false) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (Number.isNaN(d.getTime())) return String(isoStr);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return conAnio ? `${dd}/${mm}/${d.getFullYear()}` : `${dd}/${mm}`;
}
function labelEstado(e) { return { borrador: 'Borrador', pendiente: 'En curso', finalizado: 'Finalizado', cancelado: 'Cancelado' }[e] || e; }
function pillEstado(e) { return { borrador: 'pill-draft', pendiente: 'pill-pend', finalizado: 'pill-final', cancelado: 'pill-cancel' }[e]; }
function dotEstado(e) { return { borrador: 's-draft', pendiente: 's-pend', finalizado: 's-final', cancelado: 's-cancel' }[e]; }
function labelEstadoItem(item) { return { pagado: 'Pagado', parcial: 'Parcial', pendiente: 'Pendiente' }[estadoItem(item)]; }
function otrosItems(item) { return selectedPlan.value ? selectedPlan.value.items.filter((x) => x.id !== item.id) : []; }

// --- Interacción UI ---
// Toggle: tocar un plan lo abre; tocar el que ya está abierto lo cierra.
function seleccionarPlan(id) {
  selectedPlanId.value = selectedPlanId.value === id ? null : id;
  picked.value = {};
  bulkMode.value = null;
  moving.value = null;
}
function toggleItem(id) { expandedItems.value[id] = !expandedItems.value[id]; if (!expandedItems.value[id] && moving.value?.itemId === id) moving.value = null; }
function togglePick(id) { picked.value[id] = !picked.value[id]; }

function nuevoPlan() {
  const id = store.crearPlan(props.paciente.id);
  if (id) { seleccionarPlan(id); toastSuccess('✓ Plan creado'); }
}

function cambiarEstado(estado) { store.cambiarEstadoPlan(props.paciente.id, selectedPlanId.value, estado); }

function deshacerPlan() {
  confirmar('¿Deshacer este plan? (no tiene pagos)', () => {
    store.deshacerPlan(props.paciente.id, selectedPlanId.value);
  }, { textoBoton: 'Sí, deshacer', peligro: true });
}

function cambiarDescuento(item, valor) {
  if (!verificarPermisoPago()) return;
  store.actualizarDescuentoEnPlan(props.paciente.id, selectedPlanId.value, item.id, valor);
}

function registrarPago(item) {
  if (!verificarPermisoPago()) return;
  const monto = pagosTemp.value[item.id];
  if (!monto || monto <= 0) return;
  if (monto > faltaItem(item)) {
    alerta(`El monto (${formatCurrency(monto)}) supera lo que falta (${formatCurrency(faltaItem(item))}).`);
    return;
  }
  const metodo = facturacion.value ? (pagoMetodos.value[item.id] || 'efectivo') : null;
  if (store.registrarPagoEnPlan(props.paciente.id, selectedPlanId.value, item.id, monto, metodo)) {
    toastSuccess('✓ Pago registrado');
  }
  pagosTemp.value[item.id] = '';
}

function eliminarPago(item, pago) {
  if (!verificarPermisoPago()) return;
  confirmar('¿Eliminar este pago?', () => {
    store.eliminarPagoDelPlan(props.paciente.id, selectedPlanId.value, item.id, pago.id);
  }, { textoBoton: 'Sí, eliminar', peligro: true });
}

function iniciarMover(item, pago) { moving.value = { itemId: item.id, pagoId: pago.id }; moveDest.value = otrosItems(item)[0]?.id || null; }
function confirmarMover(item, pago) {
  if (!moveDest.value) return;
  const destino = selectedPlan.value.items.find((x) => x.id === moveDest.value);
  if (destino && pago.monto > faltaItem(destino)) {
    alerta(`El pago (${formatCurrency(pago.monto)}) supera lo que falta en "${destino.nombre}" (${formatCurrency(faltaItem(destino))}).`);
    return;
  }
  store.moverPagoEnPlan(props.paciente.id, selectedPlanId.value, item.id, moveDest.value, pago.id);
  moving.value = null;
}

function quitarTratamiento(item) {
  confirmar('¿Quitar este tratamiento del plan?', () => {
    store.quitarTratamientoDelPlan(props.paciente.id, selectedPlanId.value, item.id);
  }, { textoBoton: 'Sí, quitar', peligro: true });
}

function iniciarCobro(modo) {
  if (!verificarPermisoPago()) return;
  bulkMode.value = modo;
  bulkMonto.value = Math.round(faltaBulk.value);
  bulkMetodo.value = 'efectivo';
}
function confirmarCobroBloque() {
  const monto = Number(bulkMonto.value);
  if (!(monto > 0)) { alerta('Ingresá un monto mayor a 0.'); return; }
  const items = bulkMode.value === 'sel'
    ? selectedPlan.value.items.filter((it) => picked.value[it.id] && faltaItem(it) > 0)
    : selectedPlan.value.items.filter((it) => faltaItem(it) > 0);
  const ids = items.map((it) => it.id);
  const metodo = facturacion.value ? bulkMetodo.value : null;
  if (store.cobrarPlanEnBloque(props.paciente.id, selectedPlanId.value, ids, monto, metodo)) {
    toastSuccess('✓ Cobro registrado');
  }
  bulkMode.value = null;
  picked.value = {};
}

// --- Biblioteca ---
function abrirBiblioteca() { if (!verificarPermisoPago()) return; mostrarBiblioteca.value = true; }
function cerrarBiblioteca() { mostrarBiblioteca.value = false; nuevoTratamiento.value = { nombre: '', precio: '' }; busquedaBiblio.value = ''; }
function crearTratamiento() {
  if (store.agregarTratamientoGlobal(nuevoTratamiento.value)) toastSuccess('✓ Tratamiento agregado a la biblioteca');
  nuevoTratamiento.value = { nombre: '', precio: '' };
}
function iniciarEdicionTratamiento(t) { editingTreatmentId.value = t.id; tempTreatment.value = { nombre: t.nombre, precio: t.precio }; }
function cancelarEdicionTratamiento() { editingTreatmentId.value = null; tempTreatment.value = { nombre: '', precio: '' }; }
function guardarEdicionTratamiento() {
  if (editingTreatmentId.value) {
    if (store.editarTratamientoGlobal(editingTreatmentId.value, tempTreatment.value)) toastSuccess('✓ Tratamiento actualizado');
    cancelarEdicionTratamiento();
  }
}
function agregarAlPlan(t) {
  if (store.agregarTratamientoAlPlan(props.paciente.id, selectedPlanId.value, t)) {
    toastSuccess('✓ Agregado al plan');
  }
}

// --- Impresión (solo el plan seleccionado, o los tratamientos tildados) ---
function imprimirPlan() {
  const plan = selectedPlan.value;
  if (!plan) return;
  const seleccionados = plan.items.filter((it) => picked.value[it.id]);
  const items = seleccionados.length ? seleccionados : plan.items;
  const total = items.reduce((s, it) => s + precioFinalItem(it), 0);
  const pagado = items.reduce((s, it) => s + pagadoItem(it), 0);
  const saldo = Math.max(0, total - pagado);
  const parcial = seleccionados.length > 0 && seleccionados.length < plan.items.length;
  const fecha = formatDate(new Date().toISOString(), true);

  const filas = items.map((it) => {
    const estCls = { pagado: 'e-pagado', parcial: 'e-parcial', pendiente: 'e-pendiente' }[estadoItem(it)];
    const precioCel = (Number(it.descuento) > 0)
      ? `<span style="text-decoration:line-through;color:#aaa;font-size:11px">${formatCurrency(it.precio)}</span> ${formatCurrency(precioFinalItem(it))}`
      : formatCurrency(precioFinalItem(it));
    return `<tr>
      <td>${escapar(it.nombre)}</td>
      <td class="r">${precioCel}</td>
      <td class="r">${formatCurrency(pagadoItem(it))}</td>
      <td class="r">${formatCurrency(faltaItem(it))}</td>
      <td class="r"><span class="est ${estCls}">${labelEstadoItem(it)}</span></td>
    </tr>`;
  }).join('');

  const html = `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Presupuesto - ${escapar(props.paciente.nombre || '')}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box}
      body{font-family:'Segoe UI',Tahoma,sans-serif;padding:28px;color:#333}
      .head{text-align:center;border-bottom:3px solid #678AB2;padding-bottom:16px;margin-bottom:18px}
      .head h1{color:#678AB2;font-size:24px}
      .head .num{color:#8794a5;font-size:13px;font-weight:600;margin-top:3px}
      .info{display:flex;justify-content:space-between;gap:14px;flex-wrap:wrap;background:#f8f9fb;border-radius:8px;padding:12px 14px;margin-bottom:18px}
      .info .k{font-size:10.5px;text-transform:uppercase;letter-spacing:.05em;color:#8a94a2}
      .info .v{font-weight:600;font-size:14px}
      table{width:100%;border-collapse:collapse;margin-bottom:16px;font-size:13px}
      th{background:#678AB2;color:#fff;text-align:left;padding:9px 8px}
      th.r,td.r{text-align:right}
      td{padding:9px 8px;border-bottom:1px solid #edf0f4}
      tr:nth-child(even) td{background:#fafbfc}
      .est{font-size:11px;font-weight:700;padding:2px 8px;border-radius:999px}
      .e-pagado{background:#e2f4ea;color:#1f9d57}.e-parcial{background:#fbeed6;color:#d68910}.e-pendiente{background:#f8e4e4;color:#d64545}
      .tot{display:flex;justify-content:flex-end}
      .tot table{width:auto;min-width:250px}
      .tot td{border:none;padding:4px 8px}
      .tot .big td{font-size:16px;font-weight:700;border-top:2px solid #678AB2;padding-top:8px}
      .foot{margin-top:26px;text-align:center;font-size:11px;color:#9aa4b1;border-top:1px solid #edf0f4;padding-top:14px}
    </style></head><body>
    <div class="head"><h1>PRESUPUESTO</h1><div class="num">Plan Nº ${plan.num} · ${formatDate(plan.fecha, true)}${parcial ? ' · selección' : ''}</div></div>
    <div class="info">
      <div><div class="k">Paciente</div><div class="v">${escapar(props.paciente.nombre || '')}</div></div>
      <div><div class="k">Cédula</div><div class="v">${escapar(props.paciente.ci || '-')}</div></div>
      <div><div class="k">Estado del plan</div><div class="v">${labelEstado(plan.estado)}</div></div>
    </div>
    <table><thead><tr><th>Tratamiento</th><th class="r">Precio</th><th class="r">Pagado</th><th class="r">Saldo</th><th class="r">Estado</th></tr></thead>
    <tbody>${filas}</tbody></table>
    <div class="tot"><table>
      <tr><td>Total</td><td class="r">${formatCurrency(total)}</td></tr>
      <tr><td>Pagado</td><td class="r">${formatCurrency(pagado)}</td></tr>
      <tr class="big"><td>Saldo</td><td class="r">${formatCurrency(saldo)}</td></tr>
    </table></div>
    <div class="foot">Documento generado el ${fecha}</div>
    </body></html>`;

  imprimirDocumentoHTML(html);
}

function escapar(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
</script>

<style scoped>
.empty-msg { color: #9ca3af; font-size: 14px; text-align: center; padding: 16px; }
.col-titulo { font-size: 12px; font-weight: 700; color: #6b7280; margin: 0 -12px 10px; padding: 0 12px 8px; border-bottom: 1px solid #e5e7eb; text-transform: uppercase; letter-spacing: .04em; }
.detalle-vacio { color: #9ca3af; font-size: 14px; text-align: center; padding: 24px 12px; }

/* Una columna en el cel; dos columnas en compu (lista | detalle), como el mockup. */
/* Dos cajas blancas (lista | plan). El gris va en el fondo de la tarjeta (abajo),
   así las cajas no pierden tamaño por un padding extra. Sirve en cel y en compu. */
.planes-layout {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.planes-col, .detalle-col {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px;
}
.planes-col .btn-nuevo-plan { margin-bottom: 0; }
.planes-layout .plan-detalle { border-top: none; padding-top: 0; }

/* En compu: las dos cajas lado a lado. */
@media (min-width: 900px) {
  .planes-layout { display: grid; grid-template-columns: 300px 1fr; gap: 16px; align-items: start; }
  .planes-col, .detalle-col { padding: 14px; }
  .planes-col .col-titulo,
  .detalle-col .detalle-head { margin-left: -14px; margin-right: -14px; padding-left: 14px; padding-right: 14px; }
}

/* Lista de planes */
.planes-lista { display: flex; flex-direction: column; gap: 8px; margin-bottom: 10px; }
.plan-card { text-align: left; width: 100%; cursor: pointer; font-family: inherit; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 11px 12px; display: flex; flex-direction: column; gap: 6px; }
.plan-card:hover { border-color: #cbd5e1; background: #eef2f7; }
.plan-card.activo { border-color: #678AB2; background: #eef4fb; }
.plan-top { display: flex; align-items: center; gap: 8px; }
.plan-num { font-weight: 600; font-size: 14px; flex: 1; }
.plan-sub { font-size: 12px; color: #64748b; }
.plan-meta { display: flex; justify-content: space-between; font-size: 12px; }
.plan-fecha { color: #94a3b8; }
.plan-saldo { color: #64748b; }
.plan-saldo b { color: #111827; }

.dot { width: 9px; height: 9px; border-radius: 50%; flex: none; }
.s-final { background: #1f9d57; } .s-pend { background: #d68910; } .s-draft { background: #8593a5; } .s-cancel { background: #94a3b8; }
.pill { font-size: 11px; font-weight: 700; padding: 2px 9px; border-radius: 999px; white-space: nowrap; }
.pill-final { background: #e2f4ea; color: #1f9d57; }
.pill-pend { background: #fbeed6; color: #d68910; }
.pill-draft { background: #eceff3; color: #8593a5; }
.pill-cancel { background: #eceff3; color: #94a3b8; text-decoration: line-through; }

.btn-nuevo-plan { border: 1px dashed #cbd5e1; background: transparent; color: #4f6f96; border-radius: 10px; padding: 10px; font-size: 13px; cursor: pointer; font-weight: 600; width: 100%; margin-bottom: 12px; }
.btn-nuevo-plan:hover { background: #eef4fb; border-color: #678AB2; border-style: solid; }

/* Detalle del plan */
.plan-detalle { border-top: 1px solid #eef0f3; padding-top: 12px; }
.detalle-head { display: flex; align-items: center; justify-content: space-between; margin: 0 -12px 10px; padding: 0 12px 10px; border-bottom: 1px solid #e5e7eb; flex-wrap: wrap; gap: 8px; }
.detalle-titulo { display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; }
.detalle-fecha { font-size: 12px; color: #94a3b8; }
.detalle-head h3 { font-size: 16px; margin: 0; color: #111827; }
.estado-sel-wrap { font-size: 12px; color: #6b7280; }
.estado-sel { font-family: inherit; font-size: 12px; border: 1px solid #e5e7eb; border-radius: 6px; padding: 3px 6px; margin-left: 4px; }

/* Barra de cobro */
.bulkbar { display: flex; align-items: center; gap: 8px; padding: 8px 10px; background: #f1f5f9; border: 1px solid #e5e7eb; border-radius: 8px; margin-bottom: 10px; flex-wrap: wrap; }
.bulk-txt { font-size: 12.5px; color: #64748b; flex: 1; min-width: 120px; }
.bulk-txt b { color: #111827; }
.help { display: inline-grid; place-items: center; width: 24px; height: 24px; border-radius: 50%; background: #cbd5e1; color: #fff; font-size: 13px; font-weight: 700; cursor: pointer; border: none; font-family: inherit; flex: none; }
.help.activo { background: #678AB2; }
.ayuda-cobro { font-size: 12.5px; color: #475569; background: #f1f5f9; border: 1px solid #e2e8f0; border-radius: 8px; padding: 9px 11px; margin: -4px 0 10px; }

/* Items */
.items { display: flex; flex-direction: column; gap: 8px; margin-bottom: 8px; }
.item { border: 1px solid #e5e7eb; border-radius: 10px; overflow: hidden; }
.item.sel { border-color: #678AB2; box-shadow: 0 0 0 1px #678AB2 inset; }
.item-row { display: flex; align-items: center; gap: 10px; padding: 10px 12px; cursor: pointer; }
.item-row:hover { background: #f8fafc; }
.chk { width: 18px; height: 18px; flex: none; accent-color: #678AB2; cursor: pointer; }
.item-main { flex: 1; min-width: 0; }
.item-nombre { font-weight: 600; font-size: 14px; color: #111827; }
.item-badges { display: flex; align-items: center; gap: 8px; margin-top: 3px; flex-wrap: wrap; }
.badge-sm { font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 999px; }
.b-pagado { background: #e2f4ea; color: #1f9d57; } .b-parcial { background: #fbeed6; color: #d68910; } .b-pendiente { background: #f8e4e4; color: #d64545; }
.b-desc { background: #efe7fd; color: #8b5cf6; }
.falta { font-size: 12px; color: #6b7280; }
.falta b { color: #d68910; }
.item-precio { text-align: right; font-weight: 600; font-size: 14px; white-space: nowrap; }
.item-precio .tach { display: block; font-size: 11px; color: #9ca3af; text-decoration: line-through; font-weight: 400; }
.exp { color: #94a3b8; font-size: 12px; width: 14px; text-align: center; }

.item-body { padding: 6px 12px 14px 40px; border-top: 1px dashed #e5e7eb; background: #f8fafc; display: flex; flex-direction: column; gap: 10px; }
/* En el cel: menos indentación y menos gaps, para que el monto no se desborde sobre ↔/×. */
@media (max-width: 600px) {
  .item-body { padding-left: 12px; }
  .ph-row { gap: 8px; }
  .ph-fecha { width: 40px; }
}
.row-line { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.mini-label { font-size: 11px; text-transform: uppercase; letter-spacing: .05em; color: #94a3b8; font-weight: 700; }
.inp { border: 1px solid #cbd5e1; border-radius: 8px; padding: 7px 9px; font-size: 13.5px; font-family: inherit; }
.inp.chico { width: 58px; }
.inp-monto { width: 84px; max-width: 40%; }
.btn-mini { border: 1px solid #cbd5e1; background: #fff; color: #111827; border-radius: 8px; padding: 7px 12px; font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit; }
.btn-mini:hover:not(:disabled) { border-color: #678AB2; color: #4f6f96; }
.btn-mini.pri { background: #678AB2; border-color: #678AB2; color: #fff; }
.btn-mini.pri:hover:not(:disabled) { background: #4f6f96; color: #fff; }
.btn-mini:disabled { opacity: .45; cursor: not-allowed; }

.pagos-hist { display: flex; flex-direction: column; }
.ph-row { display: flex; align-items: center; gap: 10px; font-size: 13px; padding: 6px 0; border-bottom: 1px dashed #e5e7eb; }
.ph-row:last-child { border-bottom: none; }
.ph-row.vacio { color: #9ca3af; }
.ph-fecha { color: #64748b; width: 46px; flex: none; }
.ph-mid { flex: 1; min-width: 0; display: flex; flex-direction: column; line-height: 1.25; }
.ph-desc { display: block; }
.ph-tag { color: #94a3b8; font-size: 11px; }
.metodo-chip { display: inline-block; font-size: 11px; font-weight: 600; background: #eef4fb; color: #4f6f96; border-radius: 999px; padding: 1px 8px; }
.ph-monto { color: #1f9d57; font-weight: 700; font-size: 14px; }
.ph-acciones { display: flex; gap: 6px; flex: none; }
.ph-move, .ph-x { border: none; background: transparent; cursor: pointer; width: 36px; height: 36px; border-radius: 8px; display: inline-grid; place-items: center; line-height: 1; padding: 0; font-family: inherit; }
.ph-move { color: #4f6f96; font-size: 24px; }
.ph-move:hover, .ph-move:active { background: #eef4fb; }
.ph-x { color: #d64545; font-size: 24px; }
.ph-x:hover, .ph-x:active { background: #fdeaea; }
.mover-inline { display: flex; align-items: center; gap: 8px; width: 100%; background: #eef4fb; border-radius: 8px; padding: 8px 10px; flex-wrap: wrap; }
.mv-lbl { font-size: 12px; color: #4f6f96; font-weight: 600; }
.item-quitar { align-self: flex-start; border: none; background: transparent; color: #e74c3c; font-size: 12.5px; cursor: pointer; font-family: inherit; padding: 0; }
.item-quitar:hover { text-decoration: underline; }

/* Resumen */
.resumen { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin: 14px 0; background: #f8f9fa; padding: 12px; border-radius: 8px; }
.resumen-card { display: flex; flex-direction: column; align-items: center; min-width: 0; }
.resumen-card .label { font-size: 12px; color: #666; text-transform: uppercase; }
.resumen-card .valor { font-weight: bold; font-size: 16px; }
.verde { color: #27ae60; } .ambar { color: #d68910; }
/* En el cel: cada total en su propia fila (etiqueta izq, valor der), para que no se
   toquen sin importar lo largo del monto ("UYU 12.500"). Va DESPUES de la base a
   proposito: en CSS, con misma especificidad gana la regla que aparece despues. */
@media (max-width: 600px) {
  .resumen { grid-template-columns: 1fr; gap: 4px; padding: 10px 14px; }
  .resumen-card { flex-direction: row; justify-content: space-between; align-items: baseline; }
  .resumen-card .valor { font-size: 15px; }
  .resumen-card .label { font-size: 12px; }
}

/* Acciones */
.acciones { display: flex; gap: 8px; flex-wrap: wrap; }
.btn-action { background: #eaf1ff; color: #678AB2; border: none; padding: 8px 12px; border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 13px; }
.btn-action:hover { background: #dbeafe; }
.btn-action.peligro { background: #fdeaea; color: #d64545; margin-left: auto; }
.btn-action.peligro:hover { background: #f8dcdc; }

/* Modal biblioteca */
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 16px; }
.modal-content { background: #fff; border-radius: 12px; width: 100%; max-width: 480px; max-height: 90dvh; overflow-y: auto; padding: 20px; }
.modal-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; padding-bottom: 12px; border-bottom: 1px solid #e5e7eb; }
.modal-header h3 { margin: 0; font-size: 18px; }
.btn-close { border: none; background: transparent; font-size: 24px; cursor: pointer; color: #94a3b8; line-height: 1; }
.section-title { font-size: 13px; color: #6b7280; margin: 12px 0 8px; }
.buscar-biblio { width: 100%; border: 1px solid #cbd5e1; border-radius: 8px; padding: 8px 10px; font-size: 14px; font-family: inherit; margin: 8px 0; }
.form-row { display: flex; gap: 8px; flex-wrap: wrap; }
.input-modern, .input-small { border: 1px solid #cbd5e1; border-radius: 8px; padding: 8px 10px; font-size: 14px; font-family: inherit; }
.input-modern { flex: 1; min-width: 120px; }
.input-small { width: 110px; }
.btn-save-modern { background: #678AB2; color: #fff; border: none; border-radius: 8px; padding: 8px 14px; font-weight: 600; cursor: pointer; }
.btn-save-modern:disabled { opacity: .5; cursor: not-allowed; }
.global-items { display: flex; flex-direction: column; gap: 8px; max-height: 260px; overflow-y: auto; }
.global-item { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 8px 10px; background: #f8fafc; border-radius: 8px; flex-wrap: wrap; }
.global-info { flex: 1; min-width: 0; }
.info-display { display: flex; justify-content: space-between; gap: 10px; }
.t-nombre { font-weight: 600; font-size: 14px; }
.t-precio { color: #678AB2; font-weight: 600; }
.edit-global-row { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
.global-actions { display: flex; gap: 4px; }
.btn-add-small { background: #eaf1ff; color: #678AB2; border: none; border-radius: 6px; padding: 6px 10px; font-weight: 600; cursor: pointer; font-size: 13px; }
.btn-icon-action { border: none; background: transparent; cursor: pointer; font-size: 15px; padding: 4px; }
.btn-danger { filter: grayscale(0); }
/* En el cel: la fila de biblioteca en UNA sola línea. El nombre largo se recorta con
   "…" (no empuja al precio), el precio se achica un toque y no se parte, y la fila no
   wrappea, así el "UYU 3.000" no cae debajo de "Agregar" en pantallas angostas (S25). */
@media (max-width: 600px) {
  .global-item { flex-wrap: nowrap; gap: 6px; }
  .global-info { min-width: 0; }
  .info-display { gap: 6px; }
  .t-nombre { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .t-precio { white-space: nowrap; font-size: 13px; flex: none; }
  .global-actions { flex: none; gap: 2px; }
  .btn-add-small { padding: 6px 8px; }
  .btn-icon-action { font-size: 14px; padding: 4px 2px; }
}

/* Header con fondo gris, igual que Datos/Turnos/Historia/Archivos. */
:deep(.card) {
  padding: 0 !important;
  overflow: hidden;
}
:deep(.card-header) {
  background-color: #f8f9fa;
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 0 !important;
}
:deep(.card-body) {
  padding: 16px;
}
</style>
